import uuidv4 from 'src/utils/uuidv4';
import {
  ICOMX_HOST_COMMAND_TYPES,
  ICOMX_IFRAME_EVENT_TYPES,
  ICOMX_PROTOCOL_VERSION,
  IcomxBridgeCommandError,
  IcomxErrorPayload,
  IcomxBridgeState,
  IcomxCommandResult,
  IcomxHostCommandEnvelope,
  IcomxHostCommandPayloadMap,
  IcomxHostCommandType,
  IcomxIframeEventEnvelope,
  IcomxIframeEventType,
  IcomxSecurityRejection,
} from './types';

type Listener = (state: IcomxBridgeState) => void;

interface PendingRequest {
  commandType: IcomxHostCommandType;
  expectedTerminalType: IcomxIframeEventType;
  resolve: (value: IcomxIframeEventEnvelope) => void;
  reject: (reason?: unknown) => void;
  timeoutId: ReturnType<typeof setTimeout>;
}

interface CreateIcomxBridgeOptions {
  onSecurityRejection?: (rejection: IcomxSecurityRejection) => void;
  onBridgeError?: (error: Error) => void;
  commandTimeoutMs?: number;
}

interface UpdateBridgeContextInput {
  iframeOrigin?: string | null;
  iframeWindow?: Window | null;
}

const TERMINAL_EVENT_BY_COMMAND: Record<IcomxHostCommandType, IcomxIframeEventType> = {
  'comx.host.open_contact': 'comx.iframe.contact_opened',
  'comx.host.start_direct_conversation': 'comx.iframe.conversation_opened',
  'comx.host.open_last_conversation': 'comx.iframe.conversation_opened',
  'comx.host.upsert_contact': 'comx.iframe.ack',
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function isKnownHostCommandType(value: unknown): value is IcomxHostCommandType {
  return typeof value === 'string' && ICOMX_HOST_COMMAND_TYPES.includes(value as IcomxHostCommandType);
}

function isKnownIframeEventType(value: unknown): value is IcomxIframeEventType {
  return typeof value === 'string' && ICOMX_IFRAME_EVENT_TYPES.includes(value as IcomxIframeEventType);
}

function isWindowLike(value: unknown): value is Window {
  return !!value && typeof value === 'object' && 'postMessage' in value;
}

function createCommandError(message: string, details?: Partial<IcomxBridgeCommandError>) {
  const error = new Error(message) as IcomxBridgeCommandError;

  if (details?.code) {
    error.code = details.code;
  }

  if (details?.requestId) {
    error.requestId = details.requestId;
  }

  return error;
}

function isValidCommandPayload<TType extends IcomxHostCommandType>(
  type: TType,
  payload: unknown
): payload is IcomxHostCommandPayloadMap[TType] {
  if (!isRecord(payload)) {
    return false;
  }

  if (type === 'comx.host.upsert_contact') {
    return true;
  }

  if (type === 'comx.host.open_last_conversation') {
    return (
      typeof payload.contactUuid === 'string' &&
      payload.contactUuid.length > 0 &&
      (payload.fallbackToDirect === undefined || typeof payload.fallbackToDirect === 'boolean')
    );
  }

  return typeof payload.contactUuid === 'string' && payload.contactUuid.length > 0;
}

function isValidEventPayload(type: IcomxIframeEventType, payload: unknown) {
  if (!isRecord(payload)) {
    return false;
  }

  switch (type) {
    case 'comx.iframe.ready':
    case 'comx.iframe.ack':
    case 'comx.iframe.popup_blocked':
    case 'comx.iframe.popup_closed':
      return true;
    case 'comx.iframe.error':
      return !payload.message || typeof payload.message === 'string';
    case 'comx.iframe.contact_opened':
      return typeof payload.contactUuid === 'string' && payload.contactUuid.length > 0;
    case 'comx.iframe.conversation_opened':
      return (
        typeof payload.conversationUuid === 'string' &&
        payload.conversationUuid.length > 0 &&
        (payload.created === undefined || typeof payload.created === 'boolean')
      );
    default:
      return false;
  }
}

export function createIcomxBridge({
  onSecurityRejection,
  onBridgeError,
  commandTimeoutMs = 15000,
}: CreateIcomxBridgeOptions = {}) {
  let iframeOrigin: string | null = null;
  let iframeWindow: Window | null = null;
  let lastEvent: IcomxIframeEventEnvelope | null = null;
  let isReady = false;

  const listeners = new Set<Listener>();
  const pendingRequests = new Map<string, PendingRequest>();

  function emit() {
    const state: IcomxBridgeState = {
      iframeOrigin,
      hasIframeWindow: !!iframeWindow,
      isReady,
      pendingCount: pendingRequests.size,
      lastEvent,
    };

    listeners.forEach((listener) => listener(state));
  }

  function rejectSecurity(rejection: IcomxSecurityRejection) {
    onSecurityRejection?.(rejection);
  }

  function reportBridgeError(error: Error) {
    onBridgeError?.(error);
  }

  function cleanupPendingRequest(requestId: string) {
    const pendingRequest = pendingRequests.get(requestId);

    if (!pendingRequest) {
      return;
    }

    clearTimeout(pendingRequest.timeoutId);
    pendingRequests.delete(requestId);
    emit();
  }

  function reset(reason = 'The iComX widget is no longer available') {
    Array.from(pendingRequests.entries()).forEach(([requestId, pendingRequest]) => {
      clearTimeout(pendingRequest.timeoutId);
      pendingRequest.reject(createCommandError(reason, { requestId }));
      pendingRequests.delete(requestId);
    });

    lastEvent = null;
    isReady = false;
    emit();
  }

  return {
    subscribe(listener: Listener) {
      listeners.add(listener);
      listener({
        iframeOrigin,
        hasIframeWindow: !!iframeWindow,
        isReady,
        pendingCount: pendingRequests.size,
        lastEvent,
      });

      return () => {
        listeners.delete(listener);
      };
    },

    getSnapshot(): IcomxBridgeState {
      return {
        iframeOrigin,
        hasIframeWindow: !!iframeWindow,
        isReady,
        pendingCount: pendingRequests.size,
        lastEvent,
      };
    },

    updateContext({ iframeOrigin: nextOrigin, iframeWindow: nextWindow }: UpdateBridgeContextInput) {
      const didWindowChange = nextWindow !== undefined && nextWindow !== iframeWindow;
      const didOriginChange = nextOrigin !== undefined && nextOrigin !== iframeOrigin;

      if (nextOrigin !== undefined) {
        iframeOrigin = nextOrigin ?? null;
      }

      if (nextWindow !== undefined) {
        iframeWindow = nextWindow ?? null;
      }

      if (didWindowChange || didOriginChange) {
        isReady = false;
      }

      emit();
    },

    reset,

    destroy() {
      reset();
      listeners.clear();
      iframeOrigin = null;
      iframeWindow = null;
    },

    async sendCommand<TType extends IcomxHostCommandType>(
      type: TType,
      payload: IcomxHostCommandPayloadMap[TType]
    ): Promise<IcomxCommandResult<TType>> {
      if (!isKnownHostCommandType(type)) {
        const error = createCommandError(`Unsupported iComX command type: ${String(type)}`);
        reportBridgeError(error);
        throw error;
      }

      if (!iframeOrigin) {
        const error = createCommandError('iComX target origin is not available yet');
        reportBridgeError(error);
        throw error;
      }

      if (!iframeWindow) {
        const error = createCommandError('iComX iframe window is not available yet');
        reportBridgeError(error);
        throw error;
      }

      if (!isReady) {
        const error = createCommandError('iComX chat is still booting');
        reportBridgeError(error);
        throw error;
      }

      if (!isValidCommandPayload(type, payload)) {
        const error = createCommandError(`Invalid payload for ${type}`);
        reportBridgeError(error);
        throw error;
      }

      const requestId = uuidv4();
      const envelope: IcomxHostCommandEnvelope<TType> = {
        version: ICOMX_PROTOCOL_VERSION,
        type,
        requestId,
        payload,
      };

      const expectedTerminalType = TERMINAL_EVENT_BY_COMMAND[type];

      return new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          cleanupPendingRequest(requestId);
          const error = createCommandError('iComX chat request timed out', { requestId });
          reportBridgeError(error);
          reject(error);
        }, commandTimeoutMs);

        pendingRequests.set(requestId, {
          commandType: type,
          expectedTerminalType,
          resolve: resolve as (value: IcomxIframeEventEnvelope) => void,
          reject,
          timeoutId,
        });

        emit();

        try {
          iframeWindow!.postMessage(envelope, iframeOrigin!);
        } catch (error) {
          cleanupPendingRequest(requestId);
          const commandError = createCommandError('Unable to send the iComX chat command', {
            requestId,
          });
          reportBridgeError(commandError);
          reject(commandError);
        }
      });
    },

    handleMessage(event: MessageEvent<unknown>) {
      if (!iframeOrigin || event.origin !== iframeOrigin) {
        console.debug('[iComX bridge] unknown-origin', {
          expectedOrigin: iframeOrigin,
          actualOrigin: event.origin,
          sourceMatches: event.source === iframeWindow,
          isIframeWindowAvailable: Boolean(iframeWindow),
          eventData: event.data,
        });
        rejectSecurity({ reason: 'unknown-origin', event });
        return;
      }

      if (!isRecord(event.data)) {
        rejectSecurity({ reason: 'invalid-envelope', event });
        return;
      }

      const { version, type, requestId, payload } = event.data;

      if (!version || version !== ICOMX_PROTOCOL_VERSION) {
        rejectSecurity({ reason: 'unsupported-version', event, details: version });
        return;
      }

      if (!isKnownIframeEventType(type)) {
        rejectSecurity({ reason: 'unknown-type', event, details: type });
        return;
      }

      if (!iframeWindow) {
        if (type === 'comx.iframe.ready' && isWindowLike(event.source)) {
          iframeWindow = event.source;
        } else {
          console.debug('[iComX bridge] unknown-source', {
            expectedSource: iframeWindow,
            actualSource: event.source,
            eventOrigin: event.origin,
            eventData: event.data,
          });
          rejectSecurity({ reason: 'unknown-source', event });
          return;
        }
      } else if (event.source !== iframeWindow) {
        console.debug('[iComX bridge] unknown-source', {
          expectedSource: iframeWindow,
          actualSource: event.source,
          eventOrigin: event.origin,
          eventData: event.data,
        });
        rejectSecurity({ reason: 'unknown-source', event });
        return;
      }

      if (typeof requestId !== 'string' || !requestId.length) {
        rejectSecurity({ reason: 'missing-request-id', event });
        return;
      }

      if (!isValidEventPayload(type, payload)) {
        rejectSecurity({ reason: 'malformed-payload', event, details: payload });
        return;
      }

      const envelope = {
        version,
        type,
        requestId,
        payload,
      } as IcomxIframeEventEnvelope;

      if (type === 'comx.iframe.ready') {
        isReady = true;
        lastEvent = envelope;
        emit();
        return;
      }

      if (type === 'comx.iframe.popup_blocked' || type === 'comx.iframe.popup_closed') {
        lastEvent = envelope;
        emit();
        return;
      }

      const pendingRequest = pendingRequests.get(requestId);

      if (!pendingRequest) {
        rejectSecurity({ reason: 'unexpected-request', event, details: requestId });
        return;
      }

      if (type === 'comx.iframe.ack') {
        lastEvent = envelope;

        if (pendingRequest.expectedTerminalType === 'comx.iframe.ack') {
          pendingRequest.resolve(envelope);
          cleanupPendingRequest(requestId);
          return;
        }

        emit();
        return;
      }

      if (type === 'comx.iframe.error') {
        const errorPayload = payload as IcomxErrorPayload;
        const error = createCommandError(
          errorPayload.message || 'The iComX iframe rejected the request',
          {
            code: errorPayload.code,
            requestId,
          }
        );
        lastEvent = envelope;
        pendingRequest.reject(error);
        cleanupPendingRequest(requestId);
        reportBridgeError(error);
        return;
      }

      if (type !== pendingRequest.expectedTerminalType) {
        rejectSecurity({ reason: 'unexpected-request', event, details: { requestId, type } });
        return;
      }

      lastEvent = envelope;
      pendingRequest.resolve(envelope);
      cleanupPendingRequest(requestId);
    },
  };
}